import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ClientLink, ExportDropdown, Hours, InternalClientTooltip, Percent, RouteLink, Widget } from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  ReportPeriodFilter,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useClientFilters,
  useDocumentTitle,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import timeBillableTypes from '~/lookups/time-billable-types.js';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const ClientInfo = styled.div`
  margin-left: 1rem;
  line-height: 1;
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  font-weight: ${weights.bold};
`;

export default function TimeSummaryByMemberAndClient({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const searchParamsConfig = useSearchParamsConfig();

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const memberFilters = useMemberFilters();
  const timeFilters = useTimeFilters({
    timeBillableTypes: {
      ...searchParamsConfig.timeBillableTypes,
      default: [timeBillableTypes.billable, timeBillableTypes.non_billable, timeBillableTypes.internal],
    },
  });

  const [params, setParams] = useState({
    period: null,
    sort: new QuerySort('member.name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...roleFilters.filters,
    ...memberFilters.filters,
    ...timeFilters.filters,
  });

  // Init and sync URL search params
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        sort: { default: new QuerySort('member.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        roleFilters,
        timeFilters,
      ],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, roleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} data-testid="show-filters-button" />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter value={params.period} onChange={({ value }) => handleApplyFilters({ period: value })} />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;

  const metrics = useMemo(() => {
    const include = (metric) => _.isEmpty(params.timeBillableTypes) || _.includes(params.timeBillableTypes, metric);

    const metrics = [];

    if (include(timeBillableTypes.billable))
      metrics.push({ label: 'Client Billable', value: report.totals.billable, color: colors.success, key: 'billable' });
    if (include(timeBillableTypes.non_billable))
      metrics.push({
        label: 'Client Non-Billable',
        value: report.totals.nonBillable,
        color: colors.danger,
        key: 'non_billable',
      });
    if (include(timeBillableTypes.internal))
      metrics.push({ label: 'Internal', value: report.totals.internal, color: colors.warning, key: 'internal' });
    if (include(timeBillableTypes.time_off))
      metrics.push({ label: 'Time Off', value: report.totals.timeOff, color: colors.primary50, key: 'time_off' });

    return metrics;
  }, [params.timeBillableTypes, report]);

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <>
      <Report.Summary>
        <Widget>
          <Meter>
            {metrics.map((metric) => (
              <Meter.Value
                key={metric.key}
                label={metric.label}
                value={metric.value}
                color={metric.color}
                tooltip={
                  <>
                    <Hours value={metric.value} /> hours (<Percent value={metric.value / report.totals.total} />)
                  </>
                }
              />
            ))}
          </Meter>

          <Metrics>
            {metrics.map((metric) => (
              <Metric key={metric.key}>
                <RouteLink to={timeDetail({ timeBillableTypeId: metric.key })}>
                  <Metric.Value>
                    <Hours value={metric.value} />
                  </Metric.Value>
                  <Metric.Label>{metric.label}</Metric.Label>
                </RouteLink>
              </Metric>
            ))}

            <Metric>
              <RouteLink to={timeDetail()}>
                <Metric.Value>
                  <Hours value={report.totals.total} />
                </Metric.Value>
                <Metric.Label>Total</Metric.Label>
              </RouteLink>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="member.name" onSort={onSort} sort={params.sort}>
              Member
            </Table.Column>
            <Table.Column
              isVisible={
                !params.timeBillableTypes.length || _.includes(params.timeBillableTypes, timeBillableTypes.billable)
              }
              width="10rem"
              align="right">
              Client <br />
              Billable
            </Table.Column>
            <Table.Column
              isVisible={
                !params.timeBillableTypes.length || _.includes(params.timeBillableTypes, timeBillableTypes.non_billable)
              }
              width="10rem"
              align="right">
              Client <br />
              Non-Billable
            </Table.Column>
            <Table.Column
              isVisible={
                !params.timeBillableTypes.length || _.includes(params.timeBillableTypes, timeBillableTypes.internal)
              }
              width="10rem"
              align="right">
              Internal
            </Table.Column>
            <Table.Column
              isVisible={
                !params.timeBillableTypes.length || _.includes(params.timeBillableTypes, timeBillableTypes.time_off)
              }
              width="10rem"
              align="right">
              Time Off
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((member) => {
              return (
                <React.Fragment key={member.id}>
                  <Table.GroupRow top="4rem">
                    <Table.Cell>
                      <Member>{member.name}</Member>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'billable' })}>
                          <Hours value={member.billableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'non_billable' })}>
                          <Hours value={member.nonBillableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'internal' })}>
                          <Hours value={member.internalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ memberId: member.id, timeBillableTypeId: 'time_off' })}>
                          <Hours value={member.timeOffHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink
                          to={timeDetail({
                            memberId: member.id,
                          })}>
                          <Hours value={member.totalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                  </Table.GroupRow>
                  {member.groups.map((group) => {
                    const client = group.client;

                    return (
                      <Table.Row key={client ? client.id : 'time_off'}>
                        <Table.Cell>
                          <ClientInfo>
                            {client ? (
                              <>
                                <p style={{ display: 'flex' }}>
                                  <ClientLink client={client} />
                                  {client.isInternal && <InternalClientTooltip />}
                                </p>
                              </>
                            ) : (
                              <p>Time Off</p>
                            )}
                          </ClientInfo>
                        </Table.Cell>
                        <Table.Cell>
                          {client ? (
                            <RouteLink
                              to={timeDetail({
                                clientId: group.clientId ?? undefined,
                                memberId: member.id,
                                timeBillableTypeId: 'billable',
                              })}>
                              <Hours value={group.billableHours} />
                            </RouteLink>
                          ) : (
                            <Hours value={group.billableHours} />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {client ? (
                            <RouteLink
                              to={timeDetail({
                                clientId: group.clientId ?? undefined,
                                memberId: member.id,
                                timeBillableTypeId: 'non_billable',
                              })}>
                              <Hours value={group.nonBillableHours} />
                            </RouteLink>
                          ) : (
                            <Hours value={group.nonBillableHours} />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {client ? (
                            <RouteLink
                              to={timeDetail({
                                clientId: group.clientId ?? undefined,
                                memberId: member.id,
                                timeBillableTypeId: 'internal',
                              })}>
                              <Hours value={group.internalHours} />
                            </RouteLink>
                          ) : (
                            <Hours value={group.internalHours} />
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <RouteLink
                            to={timeDetail({
                              clientId: group.clientId ?? undefined,
                              memberId: member.id,
                              timeBillableTypeId: 'time_off',
                            })}>
                            <Hours value={group.timeOffHours} />
                          </RouteLink>
                        </Table.Cell>
                        <Table.Cell>
                          <RouteLink
                            to={timeDetail({
                              clientId: group.clientId ?? undefined,
                              memberId: member.id,
                              timeBillableTypeId: !client ? 'time_off' : undefined,
                            })}>
                            <Hours value={group.totalHours} />
                          </RouteLink>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </React.Fragment>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'billable',
                  })}>
                  <Hours value={report.totals.billable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'non_billable',
                  })}>
                  <Hours value={report.totals.nonBillable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'internal',
                  })}>
                  <Hours value={report.totals.internal} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink
                  to={timeDetail({
                    timeBillableTypeId: 'internal',
                  })}>
                  <Hours value={report.totals.timeOff} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail()}>
                  <Hours value={report.totals.total} />
                </RouteLink>
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Member"
            isLoading={query.status !== 'ready'}
            partialResults={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
